import { Shield, Scale, FileText, AlertTriangle, Users, Globe, Lock, Mail, Phone, MapPin } from "lucide-react";
import { Link } from "react-router-dom";

const COMPANY_INFO = {
  name: "FonderX Currency Exchange Inc.",
  address: "2-723 12th Street, New Westminster, BC V3M 4J8",
  phone: "+1 (778) 759-0400",
  email: "info@fonderx.com",
  website: "www.fonderx.com"
};

const sections = [
  {
    id: "acceptance",
    title: "1. Acceptance of Terms",
    content: `By accessing, browsing, or using the services provided by ${COMPANY_INFO.name} ("FonderX," "we," "us," or "our"), whether through our physical location, website, or any other means, you ("Customer," "you," or "your") acknowledge that you have read, understood, and agree to be bound by these Terms of Service ("Terms").

If you do not agree with any part of these Terms, you must not use our services. These Terms constitute a legally binding agreement between you and FonderX. We reserve the right to modify these Terms at any time, and your continued use of our services following any changes constitutes your acceptance of such changes.`
  },
  {
    id: "services",
    title: "2. Services Description",
    content: `FonderX provides the following services:

**Currency Exchange Services:**
• Exchange of foreign fiat currencies (USD, EUR, GBP, INR, PHP, CNY, JPY, and others)
• Purchase and sale of cryptocurrencies (Bitcoin, Ethereum, USDT, and other digital assets)
• Wire transfers and remittance services
• Business currency exchange solutions

**Service Limitations:**
• Services are available during our posted business hours
• Exchange rates are subject to change without notice based on market conditions
• We reserve the right to refuse service to any customer for any reason
• Minimum and maximum transaction limits may apply
• Certain currencies may not be available at all times`
  },
  {
    id: "eligibility",
    title: "3. Eligibility Requirements",
    content: `To use our services, you must:

• Be at least 18 years of age or the age of majority in your jurisdiction
• Provide valid government-issued identification (passport, driver's license, or other accepted ID)
• Provide proof of address if required for certain transaction amounts
• Not be listed on any government sanctions or prohibited persons lists
• Not be engaging in any illegal activity or money laundering
• Complete our Know Your Customer (KYC) verification process as required

**Identification Requirements:**
For transactions exceeding $3,000 CAD, we are required by law to verify your identity and maintain records of the transaction. For transactions exceeding $10,000 CAD, additional reporting requirements apply under FINTRAC regulations.`
  },
  {
    id: "rates",
    title: "4. Exchange Rates and Fees",
    content: `**Rate Determination:**
• Our exchange rates are determined based on current market conditions and may include a spread
• Rates displayed on our rate board or website are indicative and subject to change
• The final rate will be confirmed at the time of your transaction
• Rates for large transactions may be negotiated

**Fees and Charges:**
• Service fees may apply to certain transactions
• Wire transfer fees are charged separately and vary by destination
• All fees will be disclosed before completing your transaction
• We do not charge hidden fees

**Price Transparency:**
You will be informed of the exact amount you will receive in your target currency before confirming any transaction. If you do not agree with the offered rate, you may decline the transaction without penalty.`
  },
  {
    id: "transactions",
    title: "5. Transaction Terms",
    content: `**Transaction Completion:**
• All transactions are final once completed and cannot be reversed
• You are responsible for verifying the accuracy of all transaction details before confirmation
• Cash transactions are settled immediately upon completion
• Cryptocurrency transactions may require blockchain confirmations

**Transaction Limits:**
• Daily and monthly transaction limits may apply based on your verification level
• Large transactions may require advance notice and additional documentation
• We reserve the right to decline transactions that exceed our risk thresholds

**Cryptocurrency Specific Terms:**
• Cryptocurrency prices are highly volatile and can change rapidly
• The price quoted is only valid for a limited time (typically 15-30 seconds)
• You are responsible for providing accurate wallet addresses for cryptocurrency transfers
• We are not responsible for lost funds due to incorrect wallet addresses or blockchain network issues`
  },
  {
    id: "compliance",
    title: "6. Regulatory Compliance",
    content: `FonderX is registered with the Financial Transactions and Reports Analysis Centre of Canada (FINTRAC) as a Money Services Business (MSB).

**We comply with:**
• Proceeds of Crime (Money Laundering) and Terrorist Financing Act (PCMLTFA)
• FINTRAC reporting requirements
• Canadian sanctions regulations
• Provincial money services business regulations

**Customer Obligations:**
• You agree to provide accurate and complete information
• You certify that the funds used are not derived from illegal activities
• You agree to cooperate with any verification or compliance requirements
• You understand that we may be required to report certain transactions to authorities

**Record Keeping:**
We maintain records of all transactions as required by law, including customer identification information and transaction details. These records are kept secure and confidential.`
  },
  {
    id: "privacy",
    title: "7. Privacy and Data Protection",
    content: `Your privacy is important to us. Please refer to our separate Privacy Policy for detailed information on how we collect, use, and protect your personal information.

**Key Points:**
• We collect only the information necessary to provide our services and comply with regulations
• Your personal information is protected using industry-standard security measures
• We do not sell or share your information with third parties except as required by law
• You have the right to access and correct your personal information`
  },
  {
    id: "liability",
    title: "8. Limitation of Liability",
    content: `**FonderX's Liability:**
To the maximum extent permitted by law, FonderX shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our services.

Our total liability for any claim arising from our services shall not exceed the amount of fees paid by you for the specific transaction giving rise to the claim.

**Exclusions:**
We are not liable for:
• Losses due to market fluctuations or rate changes
• Losses due to incorrect information provided by you
• Losses due to blockchain network issues or wallet errors
• Losses due to unauthorized access to your accounts caused by your negligence
• Delays or failures caused by circumstances beyond our control

**Force Majeure:**
We shall not be liable for any delay or failure in performance resulting from acts beyond our reasonable control, including natural disasters, government actions, internet or telecommunications failures, or market disruptions.`
  },
  {
    id: "indemnification",
    title: "9. Indemnification",
    content: `You agree to indemnify, defend, and hold harmless FonderX, its officers, directors, employees, agents, and affiliates from and against any claims, damages, losses, liabilities, costs, and expenses (including reasonable legal fees) arising from:

• Your use of our services
• Your violation of these Terms
• Your violation of any applicable laws or regulations
• Any inaccurate or false information you provide
• Your negligence or willful misconduct`
  },
  {
    id: "disputes",
    title: "10. Dispute Resolution",
    content: `**Informal Resolution:**
Before initiating any formal dispute resolution process, you agree to first contact us at ${COMPANY_INFO.email} to attempt to resolve any dispute informally.

**Governing Law:**
These Terms shall be governed by and construed in accordance with the laws of the Province of British Columbia and the federal laws of Canada applicable therein.

**Jurisdiction:**
Any disputes arising from these Terms or our services shall be resolved exclusively in the courts of British Columbia, Canada.

**Complaints:**
If you have a complaint about our services, please contact us in writing. We will acknowledge your complaint within 5 business days and provide a response within 30 business days.`
  },
  {
    id: "termination",
    title: "11. Termination",
    content: `**By Customer:**
You may stop using our services at any time without notice.

**By FonderX:**
We reserve the right to suspend or terminate your access to our services at any time, with or without cause, including but not limited to:
• Violation of these Terms
• Suspected fraudulent or illegal activity
• Failure to provide required identification
• Request by law enforcement or regulatory authorities

**Effect of Termination:**
Upon termination, any pending transactions will be completed or cancelled at our discretion. You remain responsible for any fees or obligations incurred prior to termination.`
  },
  {
    id: "miscellaneous",
    title: "12. Miscellaneous",
    content: `**Entire Agreement:**
These Terms, together with our Privacy Policy, constitute the entire agreement between you and FonderX regarding our services.

**Severability:**
If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.

**Waiver:**
Our failure to enforce any right or provision of these Terms shall not be considered a waiver of those rights.

**Assignment:**
You may not assign or transfer your rights under these Terms without our prior written consent. We may assign our rights without restriction.

**Language:**
These Terms are drafted in English. If translated into another language, the English version shall prevail in case of any conflict.`
  }
];

export default function TermsOfService() {
  return (
    <div className="min-h-screen" data-testid="terms-page">
      {/* Hero Section */}
      <section className="hero-gradient py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
              <FileText className="w-6 h-6 text-[#3B82F6]" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white">
                Terms of Service
              </h1>
              <p className="text-blue-100/60 text-sm mt-1">Last Updated: February 2025</p>
            </div>
          </div>
          <p className="text-lg text-blue-100/70 leading-relaxed">
            Please read these Terms of Service carefully before using any services provided by FonderX Currency Exchange Inc.
          </p>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="bg-[#001a40] py-6 border-b border-white/10 sticky top-16 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <span className="text-blue-100/50 text-sm whitespace-nowrap">Jump to:</span>
            {sections.slice(0, 6).map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-lg text-xs text-blue-100/70 hover:text-white transition-colors whitespace-nowrap"
              >
                {section.title.split('. ')[1]}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="bg-[#002055] py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Important Notice */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6 mb-10">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-white font-semibold mb-2">Important Notice</h3>
                <p className="text-blue-100/70 text-sm leading-relaxed">
                  By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms. 
                  If you do not agree with any part of these Terms, please do not use our services.
                </p>
              </div>
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-10">
            {sections.map((section) => (
              <div key={section.id} id={section.id} className="scroll-mt-32">
                <h2 className="text-xl md:text-2xl font-bold text-white mb-4 pb-3 border-b border-white/10">
                  {section.title}
                </h2>
                <div className="text-blue-100/70 leading-relaxed space-y-4 whitespace-pre-line">
                  {section.content.split('\n\n').map((paragraph, idx) => (
                    <p key={idx} className="text-sm md:text-base">
                      {paragraph.split('**').map((text, i) => 
                        i % 2 === 1 ? <strong key={i} className="text-white">{text}</strong> : text
                      )}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Contact Information */}
          <div className="mt-16 p-8 bg-white/5 rounded-2xl border border-white/10">
            <h3 className="text-xl font-bold text-white mb-6">Contact Information</h3>
            <p className="text-blue-100/70 mb-6">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#3B82F6]" />
                <a href={`mailto:${COMPANY_INFO.email}`} className="text-blue-100/70 hover:text-white transition-colors">
                  {COMPANY_INFO.email}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#3B82F6]" />
                <a href={`tel:${COMPANY_INFO.phone.replace(/\s/g, '')}`} className="text-blue-100/70 hover:text-white transition-colors">
                  {COMPANY_INFO.phone}
                </a>
              </div>
              <div className="flex items-start gap-3 md:col-span-2">
                <MapPin className="w-5 h-5 text-[#3B82F6] flex-shrink-0 mt-0.5" />
                <span className="text-blue-100/70">{COMPANY_INFO.address}</span>
              </div>
            </div>
          </div>

          {/* Footer Links */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-white/10">
            <Link to="/privacy-policy" className="text-[#3B82F6] hover:underline text-sm">
              View our Privacy Policy →
            </Link>
            <Link to="/contact" className="text-blue-100/60 hover:text-white text-sm transition-colors">
              Have questions? Contact us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
